import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import FormHelperText from '@mui/material/FormHelperText';
import { DriverPowersensorPanel } from '../powersensor/PowersensorPanels'; // Powersensor is being retired
import Typography from '@mui/material/Typography';
import { driverCreateBody } from '@pulsepoint/shared';
import { Plus, X } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';
import { Controller, useFieldArray, useForm, type FieldErrors } from 'react-hook-form';
import type { z } from 'zod';
import { ApiError } from '../../api/client';
import {
  useApproveDriver,
  useCreateDriver,
  useDeactivateDriver,
  useDriver,
  useRejectDriver,
  useUpdateDriver,
  type DriverDetail,
} from '../../api/drivers';
import { useOrganisations } from '../../api/organisations';
import { usePermission } from '../../auth/permissions';
import { useDialog, type DialogProps } from '../../components/dialogs/DialogProvider';
import { FormDialog } from '../../components/dialogs/FormDialog';
import { applyApiError } from '../../components/form/apiErrors';
import { FieldRow, FormSection } from '../../components/form/FieldRow';
import { FormSelect } from '../../components/form/FormSelect';
import { FormTextField } from '../../components/form/FormTextField';
import { useToast } from '../../components/Toast';
import { formSaver, useLeaveGuard, useUnsavedChanges } from '../../components/unsaved/unsaved';
import { formatDate } from '../../lib/format';
import { colours, radius } from '../../theme';
import { CardsPanel, mono } from './CardsPanel';
import { BillingPanel, OdometerPanel } from './DriverPanels';
import { UnknownCards } from './UnknownCards';

type In = z.input<typeof driverCreateBody>;
type Out = z.output<typeof driverCreateBody>;
export type DriverTab = 'details' | 'vehicle' | 'cards' | 'powersensor' | 'billing';

const TAB_OF: Record<string, DriverTab> = {
  organisationId: 'details', name: 'details', email: 'details', phone: 'details', postcode: 'details', onboardedOn: 'details', notes: 'details',
  vehicleType: 'vehicle', registration: 'vehicle',
  powersensorEmail: 'powersensor', evSensorNumber: 'powersensor', serialWifiPlug: 'powersensor', serialSensorDistribution: 'powersensor', serialSensorCharger: 'powersensor',
};
const tabOf = (field: string): DriverTab | undefined => (field.startsWith('cards') ? 'cards' : TAB_OF[field]);

function defaults(d: DriverDetail | undefined, organisationId: string): In {
  const s = (v: string | null | undefined) => v ?? '';
  return {
    organisationId: d?.organisationId ?? organisationId,
    name: s(d?.name), email: s(d?.email), phone: s(d?.phone), postcode: s(d?.postcode),
    onboardedOn: d ? s(d.onboardedOn) : new Date().toLocaleDateString('en-CA'),
    vehicleType: s(d?.vehicleType), registration: s(d?.registration),
    powersensorEmail: s(d?.powersensorEmail), evSensorNumber: d?.evSensorNumber ?? 2,
    serialWifiPlug: s(d?.serialWifiPlug), serialSensorDistribution: s(d?.serialSensorDistribution), serialSensorCharger: s(d?.serialSensorCharger),
    notes: s(d?.notes),
    cards: [],
  } as unknown as In;
}

/** Opens a driver (loading it first), or the empty form for a new one. */
export function DriverDialog({ driverId, initialTab, close }: { driverId?: string; initialTab?: DriverTab } & DialogProps) {
  const { data, error } = useDriver(driverId);
  if (driverId && !data) {
    return (
      <Dialog open onClose={() => close()} fullWidth maxWidth="md" aria-label="Driver">
        <DialogContent>{error ? <Typography color="error">{error.message}</Typography> : <Skeleton variant="rounded" height={320} />}</DialogContent>
      </Dialog>
    );
  }
  return <DriverForm driver={data} initialTab={initialTab} close={close} />;
}

/**
 * A driver: Details, Vehicle (with odometer readings), Cards, Powersensor and Billing.
 * One Save for the fields; cards, readings and billing save as you change them.
 */
function DriverForm({ driver, initialTab = 'details', close }: { driver?: DriverDetail; initialTab?: DriverTab } & DialogProps) {
  const [tab, setTab] = useState<DriverTab>(initialTab);
  const [error, setError] = useState('');
  const canEdit = usePermission('drivers.edit');
  const canCards = usePermission('cards.edit');
  const canSeeBilling = usePermission('billing.view');
  const orgs = useOrganisations();
  const create = useCreateDriver();
  const update = useUpdateDriver();
  const deactivate = useDeactivateDriver();
  const approve = useApproveDriver();
  const reject = useRejectDriver();
  const dialog = useDialog();
  const toast = useToast();
  const readOnly = !canEdit || (!!driver && !driver.isActive);
  const orgOptions = (orgs.data ?? []).map((o) => ({ value: o.id, label: o.businessName }));

  const form = useForm<In, unknown, Out>({
    resolver: zodResolver(driverCreateBody),
    // Card and reading changes refresh the driver: keep what's being typed.
    values: defaults(driver, orgOptions.length === 1 ? orgOptions[0]!.value : ''),
    resetOptions: { keepDirtyValues: true },
    disabled: readOnly,
  });
  const newCards = useFieldArray({ control: form.control, name: 'cards' });
  const errs = form.formState.errors;
  const tabHasError = (t: DriverTab) => Object.keys(errs).some((k) => tabOf(k) === t);
  const showFirstProblem = (fields: string[]) => {
    const first = fields.map(tabOf).find(Boolean);
    if (first) setTab(first);
  };

  const submit = formSaver(form,
    async (v) => {
      setError('');
      try {
        if (driver) {
          const { cards: _c, ...rest } = v;
          const saved = await update.mutateAsync({ id: driver.id, ...rest });
          toast(`${saved.name} was saved.`);
          close();
        } else {
          const saved = await create.mutateAsync(v);
          toast(`${saved.name} was added${saved.cards.length ? ` with ${saved.cards.length} card${saved.cards.length === 1 ? '' : 's'}` : ''}.`);
          close();
        }
      } catch (e) {
        const known = [...Object.keys(TAB_OF), ...v.cards.map((_c, i) => `cards.${i}.tag`)];
        setError(applyApiError(e, form.setError, known));
        if (e instanceof ApiError) showFirstProblem(Object.keys(e.fields));
        return false;
      }
    },
    (e: FieldErrors) => showFirstProblem(Object.keys(e)),
  );
  useUnsavedChanges({ dirty: form.formState.isDirty, save: submit, discard: () => form.reset() });
  // A tab's own forms (cards, billing, odometer) are lost when you switch away: ask first.
  const guard = useLeaveGuard();

  const run = async (fn: () => Promise<unknown>, done: string, then?: () => void) => {
    try {
      await fn();
      toast(done);
      then?.();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.', 'error');
    }
  };

  const tabLabel = (t: DriverTab, label: string) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      {label}
      {tabHasError(t) && <Box component="span" aria-label="has a problem" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: colours.faultRed }} />}
    </Box>
  );

  const ownPanel = tab === 'cards' || tab === 'billing';
  const organisationId = form.watch('organisationId');
  // Powersensor only shows for organisations that have it turned on.
  const powersensorOn = (orgs.data ?? []).find((o) => o.id === organisationId)?.powersensorEnabled ?? driver?.powersensorEnabled ?? false;

  const review = driver?.needsReview && (
    <Alert
      severity="warning"
      action={
        canEdit && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              color="inherit"
              onClick={async () => {
                const ok = await dialog.confirm({ title: `Reject ${driver.name}'s sign-up?`, message: 'Their details and cards are deleted. Do this for spam or a duplicate.', confirmLabel: 'Reject', danger: true });
                if (ok) await run(() => reject.mutateAsync(driver.id), `${driver.name}'s sign-up was rejected.`, () => close());
              }}
            >
              Reject
            </Button>
            <Button size="small" variant="contained" onClick={() => void run(() => approve.mutateAsync(driver.id), `${driver.name} is approved. Their cards work now.`)}>
              Approve
            </Button>
          </Box>
        )
      }
      sx={{ mb: 1 }}
    >
      {driver.name} signed up {driver.onboardingLink?.label ? `with the link “${driver.onboardingLink.label}”` : 'with an onboarding link'} on {formatDate(driver.createdAt)}. Check their details and cards, then approve them. Until then their cards don't work.
    </Alert>
  );

  return (
    <FormDialog
      title={driver ? driver.name : 'Add a driver'}
      lead={driver ? review || (!driver.isActive ? <Alert severity="info">{driver.name} is deactivated: their cards don't work. Their sessions stay in reports.</Alert> : undefined) : undefined}
      submitLabel={driver ? 'Save changes' : 'Add the driver'}
      onSubmit={submit}
      onClose={() => close()}
      busy={create.isPending || update.isPending}
      error={error}
      maxWidth="md"
      minContentHeight={460}
      readOnly={readOnly || (!!driver && ownPanel && !form.formState.isDirty)}
      secondaryActions={
        driver && canEdit && !driver.needsReview ? (
          driver.isActive ? (
            <Button
              color="error"
              onClick={async () => {
                if (driver.chargerCount > 0) {
                  toast(`${driver.name} is the plug-and-charge driver on ${driver.chargerCount === 1 ? 'a charger' : `${driver.chargerCount} chargers`}. Choose someone else there first.`, 'error');
                  return;
                }
                const ok = await dialog.confirm({ title: `Deactivate ${driver.name}?`, message: "Their cards stop working. Their sessions stay in reports, and you can reactivate them later.", confirmLabel: 'Deactivate', danger: true });
                if (ok) await run(() => deactivate.mutateAsync(driver.id), `${driver.name} was deactivated.`, () => close());
              }}
            >
              Deactivate
            </Button>
          ) : (
            <Button onClick={() => void run(() => update.mutateAsync({ id: driver.id, isActive: true }), `${driver.name} is active again.`)}>Reactivate</Button>
          )
        ) : undefined
      }
      tabs={
        <Tabs value={tab} onChange={(_e, t: DriverTab) => guard.switchTab(() => setTab(t))} variant="scrollable" allowScrollButtonsMobile aria-label="Driver sections">
          <Tab value="details" label={tabLabel('details', 'Details')} />
          <Tab value="vehicle" label={tabLabel('vehicle', 'Vehicle')} />
          <Tab value="cards" label={tabLabel('cards', driver ? `Cards (${driver.cards.length})` : newCards.fields.length ? `Cards (${newCards.fields.length})` : 'Cards')} />
          {powersensorOn && <Tab value="powersensor" label={tabLabel('powersensor', 'Powersensor')} />}
          {driver && canSeeBilling && <Tab value="billing" label="Billing" />}
        </Tabs>
      }
    >
      <Box sx={{ display: tab === 'details' ? 'grid' : 'none', gap: 2.5 }}>
        {orgOptions.length > 1 || (driver && orgOptions.length === 0) ? (
          <FormSelect control={form.control} name="organisationId" label="Organisation" options={orgOptions.length ? orgOptions : [{ value: driver!.organisationId, label: driver!.organisationName }]} />
        ) : null}
        <FieldRow>
          <FormTextField control={form.control} name="name" label="Full name" autoFocus={!driver} autoComplete="off" />
          <FormTextField control={form.control} name="email" label="Email" type="email" autoComplete="off" />
        </FieldRow>
        <FieldRow columns="2fr 1fr">
          <FormTextField control={form.control} name="phone" label="Mobile" type="tel" placeholder="04XX XXX XXX" />
          <FormTextField control={form.control} name="postcode" label="Postcode" slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 4 } }} />
        </FieldRow>
        <FieldRow>
          <FormTextField control={form.control} name="onboardedOn" label="Onboarded" type="date" slotProps={{ inputLabel: { shrink: true } }} />
        </FieldRow>
        <FormTextField control={form.control} name="notes" label="Notes" multiline minRows={2} />
        {driver && driver.chargers.length > 0 && (
          <Typography color="textSecondary">
            Plug-and-charge driver on {driver.chargers.map((c) => `${c.label ?? c.chargePointId}${c.siteName ? ` (${c.siteName})` : ''}`).join(', ')}: every session there is billed to {driver.name}.
          </Typography>
        )}
      </Box>

      <Box sx={{ display: tab === 'vehicle' ? 'grid' : 'none', gap: 4 }}>
        <FieldRow columns="2fr 1fr">
          <FormTextField control={form.control} name="vehicleType" label="Make and model" placeholder="e.g. Tesla Model 3" />
          <FormTextField control={form.control} name="registration" label="Registration" slotProps={{ htmlInput: { autoCapitalize: 'characters' } }} />
        </FieldRow>
        {driver && <OdometerPanel driver={driver} />}
      </Box>

      {tab === 'cards' &&
        (driver ? (
          <CardsPanel driver={driver} />
        ) : (
          <NewCards form={form} fields={newCards} canEdit={canCards && !readOnly} organisationId={organisationId || undefined} />
        ))}

      <Box sx={{ display: tab === 'powersensor' ? 'grid' : 'none', gap: 4 }}>
        <Typography color="textSecondary">For drivers with a Powersensor at home: Pulsepoint reads their charger's energy use from it.</Typography>
        <FieldRow>
          <FormTextField control={form.control} name="powersensorEmail" label="Powersensor account email" type="email" helperText="Blank: their own email" />
          <Controller
            control={form.control}
            name="evSensorNumber"
            render={({ field }) => (
              <Box>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  // Same height as the text field beside it (theme: input line height plus 12px above and below)
                  sx={{ height: 'calc(1.4375rem + 24px)' }}
                  value={String(field.value ?? 2)}
                  onChange={(_e, v: string | null) => v && field.onChange(Number(v))}
                  disabled={field.disabled}
                  aria-labelledby="ev-sensor-label"
                >
                  {['1', '2', '3'].map((n) => <ToggleButton key={n} value={n}>Appliance {n}</ToggleButton>)}
                </ToggleButtonGroup>
                {/* Below the buttons, like the email field's helper text, so the two line up */}
                <FormHelperText id="ev-sensor-label">Which appliance is the charger</FormHelperText>
              </Box>
            )}
          />
        </FieldRow>
        <FormSection title="Serial numbers">
          <FormTextField control={form.control} name="serialWifiPlug" label="Wi-Fi plug (gateway)" />
          <FieldRow>
            <FormTextField control={form.control} name="serialSensorDistribution" label="Sensor on the switchboard" />
            <FormTextField control={form.control} name="serialSensorCharger" label="Sensor on the charger" />
          </FieldRow>
        </FormSection>
        {driver && <DriverPowersensorPanel driverId={driver.id} />}
      </Box>

      {tab === 'billing' && driver && <BillingPanel driver={driver} />}
    </FormDialog>
  );
}

/** Cards for a driver not saved yet: they're added together with the driver. */
function NewCards({
  form,
  fields,
  canEdit,
  organisationId,
}: {
  form: ReturnType<typeof useForm<In, unknown, Out>>;
  fields: ReturnType<typeof useFieldArray<In, 'cards'>>;
  canEdit: boolean;
  organisationId?: string;
}) {
  const [tag, setTag] = useState('');
  const [label, setLabel] = useState('');
  const cards = form.watch('cards') ?? [];
  const errors = form.formState.errors.cards;

  const add = (t = tag) => {
    const clean = t.replace(/[\s:]/g, '');
    if (!clean) return;
    fields.append({ tag: clean.toUpperCase(), label });
    setTag('');
    setLabel('');
  };
  const onEnter = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      add();
    }
  };

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Typography color="textSecondary">Cards are saved with the driver and work straight away.</Typography>
      {canEdit && (
        <Box role="group" aria-label="Add a card" sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1.2fr 1fr auto' }, alignItems: 'start' }}>
          {/* Plain inputs: they only feed the list below */}
          <TextFieldPlain label="Card number" value={tag} onChange={setTag} onKeyDown={onEnter} placeholder="e.g. 04A2B3C4" mono />
          <TextFieldPlain label="Label (optional)" value={label} onChange={setLabel} onKeyDown={onEnter} placeholder="e.g. Blue key fob" />
          <Button startIcon={<Plus size={18} strokeWidth={1.75} />} onClick={() => add()} sx={{ mt: { sm: 0.5 } }}>
            Add
          </Button>
        </Box>
      )}
      {fields.fields.length > 0 && (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, border: `1px solid ${colours.hairline}`, borderRadius: `${radius.panel}px` }}>
          {fields.fields.map((f, i) => (
            <Box component="li" key={f.id} sx={{ px: 2, py: 1.25, borderTop: i ? `1px solid ${colours.hairline}` : 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ ...mono, flex: '0 0 auto' }}>{cards[i]?.tag}</Typography>
                <Typography color="textSecondary" sx={{ flex: 1, minWidth: 0 }}>{cards[i]?.label}</Typography>
                <IconButton size="small" aria-label={`Remove card ${cards[i]?.tag}`} onClick={() => fields.remove(i)}>
                  <X size={18} strokeWidth={1.75} />
                </IconButton>
              </Box>
              {errors?.[i]?.tag?.message && <Typography variant="body2" color="error">{errors[i]!.tag!.message}</Typography>}
            </Box>
          ))}
        </Box>
      )}
      {canEdit && <UnknownCards organisationId={organisationId} exclude={cards.map((c) => c.tag)} onPick={(t) => add(t)} />}
    </Box>
  );
}


function TextFieldPlain({ label, value, onChange, onKeyDown, placeholder, mono: isMono }: { label: string; value: string; onChange(v: string): void; onKeyDown(e: KeyboardEvent): void; placeholder?: string; mono?: boolean }) {
  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      fullWidth
      slotProps={{ htmlInput: { spellCheck: false, autoCapitalize: isMono ? 'characters' : undefined, style: isMono ? mono : undefined } }}
    />
  );
}
